/**
 * Enhanced Teacher Dashboard Component with Agent Integration
 * Main dashboard component integrating all new features for teacher analytics
 */

import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/apiUtils';
import { 
  Story, 
  PaginatedSessions, 
  DashboardStats,
  PronunciationTrend,
  EngagementData,
  AgentUsageData,
  SafetyAlert,
  ConfigSetting
} from './dashboard/types';
import SessionDetailComponent from './dashboard/SessionDetailComponent';
import PronunciationChart from './dashboard/PronunciationChart';
import SystemConfigPanel from './SystemConfigPanel';
// import PersonaHistoryView from './dashboard/PersonaHistoryView';
import SummaryPromptsEditor from './dashboard/SummaryPromptsEditor';
import './dashboard/PersonaHistoryView.css';
import { 
  BookIcon, 
  StudentIcon, 
  ActivityIcon, 
  SettingsIcon, 
  FilterIcon, 
  PlusIcon, 
  // SearchIcon, 
  EditIcon, 
  DeleteIcon,
  EmptyIcon,
  UploadIcon,
  CloseIcon,
  // DownloadIcon,
  HomeIcon,
  LightningIcon,
  CheckCircleIcon
} from './SvgConsolidated/SvgIcons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

interface EnhancedTeacherDashboardProps {
  onReturnToHome?: () => void;
}

export default function EnhancedTeacherDashboard({ onReturnToHome }: EnhancedTeacherDashboardProps) {
  // State Management
  const [activeTab, setActiveTab] = useState<'sessions' | 'stories' | 'alerts' | 'analytics' | 'prompts'>('sessions');
  const [showConfig, setShowConfig] = useState(false);
  const [showPromptsEditor, setShowPromptsEditor] = useState(false);
  const [sessions, setSessions] = useState<PaginatedSessions>({ sessions: [], total: 0, page: 1, pageSize: 10, totalPages: 0 });
  const [stories, setStories] = useState<Story[]>([]);
  const [allStories, setAllStories] = useState<Story[]>([]);
  const [expandedTagsRowId, setExpandedTagsRowId] = useState<string | null>(null); // Store all stories for filtering
  const [gradeDrawerOpen, setGradeDrawerOpen] = useState(false);
  const [selectedStoryGrade, setSelectedStoryGrade] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [pronunciationTrends, setPronunciationTrends] = useState<PronunciationTrend[]>([]);
  const [engagementData, setEngagementData] = useState<EngagementData[]>([]);
  const [agentUsageData, setAgentUsageData] = useState<AgentUsageData[]>([]);
  const [safetyAlerts, setSafetyAlerts] = useState<SafetyAlert[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [configSettings, setConfigSettings] = useState<ConfigSetting[]>([]);
  
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedStorySessionId, setSelectedStorySessionId] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  // Replace agent filter with date range filters
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [selectedGrade, setSelectedGrade] = useState<string>('Grade 2-3');
  const [showAddStoryPopup, setShowAddStoryPopup] = useState(false);
  const [popupPage, setPopupPage] = useState<1 | 2>(1);
  const [uploadedStoryContent, setUploadedStoryContent] = useState<string>('');
  const [uploadedStoryTitle, setUploadedStoryTitle] = useState<string>('');
  const [readingLevel, setReadingLevel] = useState<string>('');
  const [lexileLevel, setLexileLevel] = useState<string>('');
  const [tags, setTags] = useState<string>('');
  const [estimatedReadingTime, setEstimatedReadingTime] = useState<string>('');
  const [author, setAuthor] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [editingStory, setEditingStory] = useState<any>(null);
  const [showBatchUploadModal, setShowBatchUploadModal] = useState(false);
  const [batchUploadContent, setBatchUploadContent] = useState<string>('');
  const [parsedStories, setParsedStories] = useState<any[]>([]);
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  // const [personaHistory, setPersonaHistory] = useState<any>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load sessions when page or filters change
  useEffect(() => {
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, filterDateFrom, filterDateTo]);

  // Update reading time when story content or grade changes
  useEffect(() => {
    if (uploadedStoryContent && selectedGrade) {
      const newReadingTime = calculateReadingTime(uploadedStoryContent, selectedGrade);
      setEstimatedReadingTime(newReadingTime);
    }
  }, [uploadedStoryContent, selectedGrade]);

  // Auto-dismiss notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000); // Dismiss after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadSessions(),
        loadStories(),
        loadDashboardStats(),
        loadAnalyticsData(),
        loadSafetyAlerts(),
        loadConfigSettings()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: '8',
        ...(searchTerm && { search: searchTerm }),
        ...(filterDateFrom && { date_from: filterDateFrom }),
        ...(filterDateTo && { date_to: filterDateTo })
      });

      const response = await fetch(getApiUrl(`/v1/dashboard/sessions?${params}`));
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadStories = async () => {
    try {
      const response = await fetch(getApiUrl('/v1/dashboard/stories'));
      if (response.ok) {
        const data = await response.json();
        const fetchedStories = data.stories || [];
        
        // Sort stories by grade level in proper order
        const gradeOrder = ['Grade K-1', 'Grade 2-3', 'Grade 4-5', 'Grade 6-7', 'Grade 8-9', 'Grade 10-12'];
        const sortedStories = fetchedStories.sort((a: any, b: any) => {
          const gradeA = a.grade_level || '';
          const gradeB = b.grade_level || '';
          
          // Find index in gradeOrder array
          const indexA = gradeOrder.findIndex(g => gradeA.includes(g));
          const indexB = gradeOrder.findIndex(g => gradeB.includes(g));
          
          // If grade not found, put at end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          
          return indexA - indexB;
        });
        
        setAllStories(sortedStories); // Store all stories
        setStories(sortedStories); // Display all initially
      }
    } catch (error) {
      console.error('Failed to load stories:', error);
      setStories([]);
      setAllStories([]);
    }
  };

  // Filter stories by selected grade
  useEffect(() => {
    if (selectedStoryGrade) {
      const filtered = allStories.filter((story: any) => 
        story.grade_level && story.grade_level.includes(selectedStoryGrade)
      );
      setStories(filtered);
    } else {
      setStories(allStories);
    }
  }, [selectedStoryGrade, allStories]);

  const loadDashboardStats = async () => {
    try {
      const response = await fetch(getApiUrl('/v1/dashboard/stats'));
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      const [pronunciationRes, engagementRes, agentUsageRes] = await Promise.all([
        fetch(getApiUrl('/v1/dashboard/analytics/pronunciation-trends')),
        fetch(getApiUrl('/v1/dashboard/analytics/engagement')),
        fetch(getApiUrl('/v1/dashboard/analytics/agent-usage'))
      ]);

      if (pronunciationRes.ok) {
        const data = await pronunciationRes.json();
        setPronunciationTrends(data.trends || []);
      }
      if (engagementRes.ok) {
        const data = await engagementRes.json();
        setEngagementData(data.engagement || []);
      }
      if (agentUsageRes.ok) {
        const data = await agentUsageRes.json();
        setAgentUsageData(data.agent_usage || []);
      }
    } catch (error) {
      console.error('Failed to load analytics data:', error);
      setPronunciationTrends([]);
      setEngagementData([]);
      setAgentUsageData([]);
    }
  };

  const loadSafetyAlerts = async () => {
    try {
      const response = await fetch(getApiUrl('/v1/dashboard/safety-alerts?resolved=false'));
      if (response.ok) {
        const data = await response.json();
        setSafetyAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to load safety alerts:', error);
      setSafetyAlerts([]);
    }
  };

  // const _loadPersonaHistory = async () => {
  //   // try {
  //   //   const response = await fetch(getApiUrl('/v1/dashboard/persona-history'));
  //   //   if (response.ok) {
  //   //     const data = await response.json();
  //   //     setPersonaHistory(data);
  //   //   }
  //   // } catch (error) {
  //   //   console.error('Failed to load persona history:', error);
  //   //   setPersonaHistory(null);
  //   // }
  // };

  const loadConfigSettings = async () => {
    try {
      const response = await fetch(getApiUrl('/v1/dashboard/config'));
      if (response.ok) {
        const data = await response.json();
        // Config API returns nested structure - flatten and map to ConfigSetting interface
        const configArray: ConfigSetting[] = Object.values(data.config || {})
          .flat()
          .map((item: any) => ({
            id: item.key || '',
            settingKey: item.key || '',
            settingValue: String(item.value || ''),
            description: item.description || '',
            category: 'agent' as const, // Default category since backend doesn't provide this mapping
            dataType: item.data_type || 'string' as const,
            updatedAt: item.updated_at || ''
          }));
        setConfigSettings(configArray);
      }
    } catch (error) {
      console.error('Failed to load config settings:', error);
      setConfigSettings([]);
    }
  };

  const handleStoryPreview = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsPreviewing(true);
    
    // Read file content for preview
    try {
      const fileContent = await file.text();
      const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      setUploadedStoryContent(fileContent);
      setUploadedStoryTitle(fileName);
      
        // Parse metadata from file content
        const parseMetadata = (content: string) => {
          const metadata = {
            author: '',
            tags: '',
            lexileLevel: '',
            readingLevel: '',
            title: '',
            storyContent: ''
          };

          // Common patterns to look for metadata (case-insensitive)
          const lines = content.split('\n');
          let storyStartIndex = -1;
          
          for (let i = 0; i < Math.min(lines.length, 20); i++) { // Check first 20 lines
            const line = lines[i].trim();
            const lowerLine = line.toLowerCase();
            
            // Handle "Title : " format
            if (lowerLine.startsWith('title :') || lowerLine.startsWith('title:')) {
              metadata.title = line.split(':')[1]?.trim() || '';
            }
            
            // Handle "Author : " format
            if (lowerLine.startsWith('author :') || lowerLine.startsWith('author:') || 
                lowerLine.startsWith('by :') || lowerLine.startsWith('by:') || 
                lowerLine.startsWith('written by :') || lowerLine.startsWith('written by:')) {
              metadata.author = line.split(':')[1]?.trim() || '';
            }
            
            // Handle "Story : " format - mark where story content begins
            if (lowerLine.startsWith('story :') || lowerLine.startsWith('story:')) {
              const storyText = line.split(':')[1]?.trim() || '';
              if (storyText) {
                metadata.storyContent = storyText;
                storyStartIndex = i;
              }
            }
            
            // Tags patterns
            if (lowerLine.startsWith('tags :') || lowerLine.startsWith('tags:') || 
                lowerLine.startsWith('keywords :') || lowerLine.startsWith('keywords:') || 
                lowerLine.startsWith('themes :') || lowerLine.startsWith('themes:')) {
              metadata.tags = line.split(':')[1]?.trim() || '';
            }
            
            // Lexile level patterns
            if (lowerLine.startsWith('lexile :') || lowerLine.startsWith('lexile:') || 
                lowerLine.startsWith('lexile level :') || lowerLine.startsWith('lexile level:') || 
                lowerLine.includes('lexile')) {
              const lexileMatch = line.match(/(\d+L?)/i);
              if (lexileMatch) {
                metadata.lexileLevel = lexileMatch[1].toUpperCase();
                if (!metadata.lexileLevel.endsWith('L')) {
                  metadata.lexileLevel += 'L';
                }
              }
            }
            
            // Reading level patterns
            if (lowerLine.startsWith('reading level :') || lowerLine.startsWith('reading level:') || 
                lowerLine.startsWith('level :') || lowerLine.startsWith('level:') || 
                lowerLine.startsWith('difficulty :') || lowerLine.startsWith('difficulty:')) {
              metadata.readingLevel = line.split(':')[1]?.trim() || '';
            }
          }

          // If we found a "Story :" line, collect the rest of the content
          if (storyStartIndex >= 0) {
            const remainingLines = lines.slice(storyStartIndex + 1);
            if (remainingLines.length > 0) {
              metadata.storyContent += (metadata.storyContent ? ' ' : '') + remainingLines.join('\n').trim();
            }
          }

          // Also check the last 10 lines for metadata
          for (let i = Math.max(0, lines.length - 10); i < lines.length; i++) {
            const line = lines[i].trim();
            const lowerLine = line.toLowerCase();
            
            if (lowerLine.startsWith('author :') || lowerLine.startsWith('author:') || 
                lowerLine.startsWith('by :') || lowerLine.startsWith('by:') || 
                lowerLine.startsWith('written by :') || lowerLine.startsWith('written by:')) {
              metadata.author = line.split(':')[1]?.trim() || '';
            }
            
            if (lowerLine.startsWith('tags :') || lowerLine.startsWith('tags:') || 
                lowerLine.startsWith('keywords :') || lowerLine.startsWith('keywords:') || 
                lowerLine.startsWith('themes :') || lowerLine.startsWith('themes:')) {
              metadata.tags = line.split(':')[1]?.trim() || '';
            }
            
            if (lowerLine.startsWith('lexile :') || lowerLine.startsWith('lexile:') || 
                lowerLine.startsWith('lexile level :') || lowerLine.startsWith('lexile level:') || 
                lowerLine.includes('lexile')) {
              const lexileMatch = line.match(/(\d+L?)/i);
              if (lexileMatch) {
                metadata.lexileLevel = lexileMatch[1].toUpperCase();
                if (!metadata.lexileLevel.endsWith('L')) {
                  metadata.lexileLevel += 'L';
                }
              }
            }
            
            if (lowerLine.startsWith('reading level :') || lowerLine.startsWith('reading level:') || 
                lowerLine.startsWith('level :') || lowerLine.startsWith('level:') || 
                lowerLine.startsWith('difficulty :') || lowerLine.startsWith('difficulty:')) {
              metadata.readingLevel = line.split(':')[1]?.trim() || '';
            }
          }

          return metadata;
        };      // Extract metadata from file content
      const metadata = parseMetadata(fileContent);
      
      // Use extracted title if available, otherwise use filename
      const finalTitle = metadata.title || fileName;
      setUploadedStoryTitle(finalTitle);
      
      // Use extracted story content if available, otherwise use full file content
      const finalContent = metadata.storyContent || fileContent;
      setUploadedStoryContent(finalContent);
      
      // Set extracted metadata or defaults
      setAuthor(metadata.author);
      setReadingLevel(metadata.readingLevel);
      setLexileLevel(metadata.lexileLevel);
      setTags(metadata.tags);
      
      // Clear the file input and move to page 2
      event.target.value = '';
      setPopupPage(2);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Error reading file content');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleStoryUpload = async () => {
    if (!uploadedStoryContent || !uploadedStoryTitle) {
      alert('Please select a story file first');
      return;
    }

    setIsUploading(true);
    
    try {
      // Create a blob from the story content
      const storyBlob = new Blob([uploadedStoryContent], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('story', storyBlob, `${uploadedStoryTitle}.txt`);
      formData.append('ageGroup', selectedGrade);
      formData.append('title', uploadedStoryTitle);
      formData.append('content', uploadedStoryContent);
      formData.append('readingLevel', readingLevel);
      formData.append('lexileLevel', lexileLevel);
      formData.append('tags', tags);
      formData.append('estimatedReadingTime', estimatedReadingTime);
      formData.append('author', author);

      const response = await fetch(getApiUrl('/v1/dashboard/story'), {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        // const result = await response.json();
        alert('Story uploaded successfully!');
        await loadStories(); // Refresh the stories list

        // Close popup and clear form
        setShowAddStoryPopup(false);
        clearStoryForm();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to upload story: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);

      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        alert('Network error: Unable to connect to the server. Please ensure the backend is running and try again.');
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        alert(`Failed to upload story: ${errorMessage}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const clearStoryForm = () => {
    setUploadedStoryContent('');
    setUploadedStoryTitle('');
    setReadingLevel('');
    setLexileLevel('');
    setTags('');
    setEstimatedReadingTime('');
    setAuthor('');
    setPopupPage(1);
    setEditingStory(null);
  };

  const calculateReadingTime = (content: string, grade: string) => {
    const wordCount = content.split(/\s+/).length;
    const getWordsPerMinute = (gradeLevel: string) => {
      switch (gradeLevel) {
        case 'Grade 2-3': return 70;
        case 'Grade 4-5': return 90;
        case 'Grade 6-8': return 120;
        case 'Grade 9-10': return 150;
        default: return 150;
      }
    };
    
    const wpm = getWordsPerMinute(grade);
    const estimatedMinutes = Math.ceil(wordCount / wpm);
    return `${estimatedMinutes} min`;
  };

  const handleGradeChange = (newGrade: string) => {
    setSelectedGrade(newGrade);
    // Reading time will be automatically recalculated by useEffect
  };

  const handleDeleteStory = async (storyId: string, storyTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${storyTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/v1/dashboard/story/${storyId}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Story deleted successfully!');
        await loadStories(); // Refresh the stories list
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to delete story: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to delete story: ${errorMessage}`);
    }
  };

  const handleEditStory = async (story: any) => {
    try {
      // Fetch full story content from the database
      const response = await fetch(getApiUrl(`/v1/dashboard/story/${story.dbId || story.id}/full`));
      let fullContent = story.content_preview || '';
      
      if (response.ok) {
        const data = await response.json();
        fullContent = data.content || story.content_preview || '';
      }

      setEditingStory(story);
      setUploadedStoryTitle(story.title);
      setUploadedStoryContent(fullContent);
      setSelectedGrade(story.grade_level || 'Grade 2-3');
      setReadingLevel(story.reading_level || 'Beginner');
      setLexileLevel(story.lexile_level || '');
      setTags(Array.isArray(story.tags) ? story.tags.join(', ') : (story.tags || ''));
      setEstimatedReadingTime(story.reading_time || '');
      setAuthor(story.author || '');
      setShowAddStoryPopup(true);
      setPopupPage(2);
    } catch (error) {
      console.error('Error fetching full story content:', error);
      // Fall back to using preview content
      setEditingStory(story);
      setUploadedStoryTitle(story.title);
      setUploadedStoryContent(story.content_preview || '');
      setSelectedGrade(story.grade_level || 'Grade 2-3');
      setReadingLevel(story.reading_level || 'Beginner');
      setLexileLevel(story.lexile_level || '');
      setTags(Array.isArray(story.tags) ? story.tags.join(', ') : (story.tags || ''));
      setEstimatedReadingTime(story.reading_time || '');
      setAuthor(story.author || '');
      setShowAddStoryPopup(true);
      setPopupPage(2);
    }
  };

  const handleUpdateStory = async () => {
    if (!editingStory || !uploadedStoryTitle.trim()) {
      alert('Please provide a story title');
      return;
    }

    setIsUploading(true);
    
    try {
      // Process tags: convert comma-separated string to JSON array
      const tagsToSend = tags ? tags.split(',').map(t => t.trim()).filter(t => t) : [];

      const updateData = {
        title: uploadedStoryTitle,
        content: uploadedStoryContent,
        grade_level: selectedGrade,
        reading_level: readingLevel,
        lexile_level: lexileLevel,
        tags: JSON.stringify(tagsToSend),
        reading_time: estimatedReadingTime,
        author: author
      };

      const response = await fetch(getApiUrl(`/v1/dashboard/story/${editingStory.dbId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        alert('Story updated successfully!');
        await loadStories(); // Refresh the stories list
        
        // Close popup and clear form
        setShowAddStoryPopup(false);
        clearStoryForm();
        setEditingStory(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to update story: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to update story: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleBatchUpload = () => {
    setShowBatchUploadModal(true);
    setBatchUploadContent('');
    setParsedStories([]);
  };

  const parseBatchContent = (content: string) => {
    const stories = [];
    const storyBlocks = content.split(/Story \d+:/);
    
    // Remove the first empty element
    storyBlocks.shift();
    
    for (let i = 0; i < storyBlocks.length; i++) {
      const block = storyBlocks[i].trim();
      if (!block) continue;
      
      const story: any = {
        gradeLevel: '',
        title: '',
        readingLevel: '',
        author: '',
        lexile: '',
        tags: '',
        content: ''
      };
      
      const lines = block.split('\n');
      let storyStartIndex = -1;
      
      for (let j = 0; j < lines.length; j++) {
        const line = lines[j].trim();
        const lowerLine = line.toLowerCase();
        
        if (lowerLine.startsWith('grade level :') || lowerLine.startsWith('grade level:')) {
          story.gradeLevel = line.split(':')[1]?.trim() || '';
        } else if (lowerLine.startsWith('title :') || lowerLine.startsWith('title:')) {
          story.title = line.split(':')[1]?.trim() || '';
        } else if (lowerLine.startsWith('reading level:') || lowerLine.startsWith('reading level :')) {
          story.readingLevel = line.split(':')[1]?.trim() || '';
        } else if (lowerLine.startsWith('author :') || lowerLine.startsWith('author:')) {
          story.author = line.split(':')[1]?.trim() || '';
        } else if (lowerLine.startsWith('lexile:') || lowerLine.startsWith('lexile :')) {
          story.lexile = line.split(':')[1]?.trim() || '';
        } else if (lowerLine.startsWith('tags:') || lowerLine.startsWith('tags :')) {
          story.tags = line.split(':')[1]?.trim() || '';
        } else if (lowerLine.startsWith('story:') || lowerLine.startsWith('story :')) {
          storyStartIndex = j;
          story.content = line.split(':')[1]?.trim() || '';
        }
      }
      
      // Collect the rest of the story content
      if (storyStartIndex >= 0) {
        const remainingLines = lines.slice(storyStartIndex + 1);
        if (remainingLines.length > 0) {
          story.content += (story.content ? ' ' : '') + remainingLines.join('\n').trim();
        }
      }
      
      // Calculate reading time
      if (story.content && story.gradeLevel) {
        story.estimatedReadingTime = calculateReadingTime(story.content, story.gradeLevel);
      }
      
      if (story.title && story.content) {
        stories.push(story);
      }
    }
    
    return stories;
  };

  const handleBatchContentChange = (content: string) => {
    setBatchUploadContent(content);
    if (content.trim()) {
      try {
        const parsed = parseBatchContent(content);
        setParsedStories(parsed);
      } catch (error) {
        console.error('Error parsing batch content:', error);
        setParsedStories([]);
      }
    } else {
      setParsedStories([]);
    }
  };

  const handleBatchFileUpload = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const content = await file.text();
      handleBatchContentChange(content);
      // Clear the file input
      event.target.value = '';
    } catch (error) {
      console.error('Error reading batch file:', error);
      alert('Error reading file content');
    }
  };

  const processBatchUpload = async () => {
    if (parsedStories.length === 0) {
      alert('No valid stories found to upload');
      return;
    }
    
    setIsBatchUploading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const story of parsedStories) {
      try {
        // Create a blob from the story content
        const storyBlob = new Blob([story.content], { type: 'text/plain' });
        const formData = new FormData();
        formData.append('story', storyBlob, `${story.title}.txt`);
        formData.append('ageGroup', story.gradeLevel);
        formData.append('title', story.title);
        formData.append('content', story.content);
        formData.append('readingLevel', story.readingLevel);
        formData.append('lexileLevel', story.lexile);
        formData.append('tags', story.tags);
        formData.append('estimatedReadingTime', story.estimatedReadingTime || '5 min');
        formData.append('author', story.author);

        const response = await fetch(getApiUrl('/v1/dashboard/story'), {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to upload story: ${story.title}`);
        }
      } catch (error) {
        errorCount++;
        console.error(`Error uploading story: ${story.title}`, error);
      }
    }
    
    setIsBatchUploading(false);
    alert(`Batch upload completed!\nSuccessful: ${successCount}\nFailed: ${errorCount}`);
    
    if (successCount > 0) {
      await loadStories(); // Refresh the stories list
    }
    
    // Close modal and clear form
    setShowBatchUploadModal(false);
    setBatchUploadContent('');
    setParsedStories([]);
  };

  const getAgentIcon = (agentType: string) => {
    switch (agentType) {
      case 'wordwizard': return '🧙‍♂️';
      case 'quizcritter': return '🦎';
      case 'roleranger': return '🤠';
      case 'evidencebuddy': return '🔍';
      case 'safetyshield': return '🛡️';
      default: return '🤖';
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Render Sessions Tab
  const renderSessionsTab = () => (
    <div className="space-y-6">
      {/* Dashboard Stats Overview */}
      {dashboardStats && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <StudentIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardStats.total_sessions || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <ActivityIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardStats.active_sessions || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <ActivityIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Duration</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {sessions.summary?.avg_duration ? `${Math.round(sessions.summary.avg_duration)}m` : 
                   (dashboardStats?.avg_session_duration ? `${Math.round(dashboardStats.avg_session_duration / 60)}m` : '0m')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <LightningIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Accuracy Rate</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardStats.accuracy_rate || 0}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Recent Sessions</h3>
        </div>
        <div className="p-6">
          {sessions.sessions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <EmptyIcon className="w-16 h-16 mx-auto" />
              </div>
              <p className="text-gray-500">No sessions found</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[380px] overflow-y-auto pr-2">
              {sessions.sessions.map(session => {
                const storySessionId = (session as any).story_session_id || 'no-story';
                return (
                <div
                  key={`${session.id}-${storySessionId}`}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedSessionId(session.id);
                    setSelectedStorySessionId((session as any).story_session_id || null);
                  }}
                >
              
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getAgentIcon((session as any).session_type || 'reading')}</span>
                      <div>
                        <h4 className="font-medium text-gray-800">{(session as any).story_title || 'Untitled Story'}</h4>
                        <p className="text-sm text-gray-500">
                          {(session as any).grade ? `Grade: ${(session as any).grade}` : `Age: ${(session as any).student_age}`} • 
                          Duration: {formatDuration(Math.round(((session as any).duration_minutes || 0) * 60))} • 
                          Completion: {Math.round(((session as any).completion_rate || 0) * 100)}%
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      (session as any).end_time ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {(session as any).end_time ? 'COMPLETED' : 'IN PROGRESS'}
                    </span>
                    <div className="text-sm text-gray-500 mt-1">
                      {new Date((session as any).start_time).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date((session as any).start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {sessions.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((sessions.page - 1) * sessions.pageSize) + 1} to {Math.min(sessions.page * sessions.pageSize, sessions.total)} of {sessions.total} sessions
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {sessions.page} of {sessions.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(sessions.totalPages, currentPage + 1))}
                  disabled={currentPage === sessions.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render Stories Tab (Enhanced from original)
  const renderStoriesTab = () => (
    <div className="space-y-6">

      {/* Stories List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-xl font-semibold text-gray-800">Story Library</h3>
            <div className="flex gap-2 items-center relative">
              <button
                onClick={() => setShowAddStoryPopup(true)}
                className="h-8 px-3 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1.5"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Story
              </button>
              <button
                onClick={handleBatchUpload}
                className="h-8 px-3 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-1.5"
              >
                <UploadIcon className="w-3.5 h-3.5" />
                Batch Upload
              </button>
              <div className="relative">
                <button
                  onClick={() => setGradeDrawerOpen(!gradeDrawerOpen)}
                  className="h-8 flex items-center gap-1.5 px-2.5 text-xs rounded bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  <FilterIcon className="w-3.5 h-3.5" />
                  {selectedStoryGrade && (
                    <span className="px-2 py-0.5 bg-purple-800 text-white rounded-full text-xs">
                      {selectedStoryGrade}
                    </span>
                  )}
                </button>
                
                {/* Grade Drawer - Positioned below Filter button */}
                {gradeDrawerOpen && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-purple-200 rounded p-2 shadow-lg z-10">
                    <h4 className="text-[11px] font-semibold text-gray-800 mb-1">Select Grade Level</h4>
                    <div className="flex flex-col gap-1">
                      {['Grade K-1', 'Grade 2-3', 'Grade 4-5', 'Grade 6-7', 'Grade 8-9', 'Grade 10-12'].map((grade) => {
                        const gradeStories = allStories.filter((s: any) => 
                          s.grade_level && s.grade_level.includes(grade)
                        );
                        const count = gradeStories.length;
                        const isSelected = selectedStoryGrade === grade;
                        
                        return (
                          <button
                            key={grade}
                            onClick={() => {
                              setSelectedStoryGrade(isSelected ? null : grade);
                              setGradeDrawerOpen(false);
                            }}
                            disabled={count === 0}
                            className={`
                              px-1.5 py-1 rounded border transition-all text-left
                              ${isSelected 
                                ? 'bg-purple-600 text-white border-purple-700 shadow-md' 
                                : count > 0
                                  ? 'bg-white border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                                  : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                              }
                            `}
                          >
                            <div className="font-semibold text-[11px]">🎓 {grade}</div>
                            <div className={`text-[9px] ${isSelected ? 'text-purple-100' : count > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
                              {count} {count === 1 ? 'story' : 'stories'}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {selectedStoryGrade && (
                <button
                  onClick={() => setSelectedStoryGrade(null)}
                  className="h-8 px-2 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 mb-[10%]">
          {stories.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <BookIcon className="w-16 h-16 mx-auto" />
              </div>
              <p className="text-gray-500 mb-3">
                {selectedStoryGrade 
                  ? `No stories found for ${selectedStoryGrade}. Try a different grade level or clear the filter.`
                  : 'No stories uploaded yet. Click "Add Story" to upload your first story!'
                }
              </p>
              {selectedStoryGrade && (
                <button
                  onClick={() => setSelectedStoryGrade(null)}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Show All Stories
                </button>
              )}
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[22%]">
                      Story Title
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                      Grade Level
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                      Lexile Level
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[22%]">
                      Tags
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                      Author
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                      Created Time
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stories.map((story, index) => (
                    <tr key={story.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 py-2">
                        <div className="text-xs font-medium text-gray-900 break-words">{story.title}</div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                          {(story as any).grade_level || story.grade || story.ageGroup || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900">
                        {(story as any).lexile_level || '-'}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(story as any).tags ? (
                            Array.isArray((story as any).tags) ? (
                              <>
                                {(expandedTagsRowId === story.id ? (story as any).tags : (story as any).tags.slice(0, 3)).map((tag: string, tagIndex: number) => (
                                  <span
                                    key={tagIndex}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {(story as any).tags.length > 3 && (
                                  <button
                                    onClick={() => setExpandedTagsRowId(expandedTagsRowId === story.id ? null : story.id)}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                  >
                                    {expandedTagsRowId === story.id ? 'Show less' : `+${(story as any).tags.length - 3} more`}
                                  </button>
                                )}
                              </>
                            ) : typeof (story as any).tags === 'string' && (story as any).tags ? (
                              <>
                                {(expandedTagsRowId === story.id 
                                  ? (story as any).tags.split(',') 
                                  : (story as any).tags.split(',').slice(0, 3)
                                ).map((tag: string, tagIndex: number) => (
                                  <span
                                    key={tagIndex}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                  >
                                    {tag.trim()}
                                  </span>
                                ))}
                                {(story as any).tags.split(',').length > 3 && (
                                  <button
                                    onClick={() => setExpandedTagsRowId(expandedTagsRowId === story.id ? null : story.id)}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                  >
                                    {expandedTagsRowId === story.id ? 'Show less' : `+${(story as any).tags.split(',').length - 3} more`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-500 text-sm">No tags</span>
                            )
                          ) : (
                            <span className="text-gray-500 text-sm">No tags</span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900">
                        {(story as any).author || 'Unknown'}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500">
                        {new Date((story as any).created_at || story.uploadDate).toLocaleDateString()}
                      </td>
                      <td className="px-2 py-2 text-xs font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditStory(story)}
                            className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 p-2 rounded transition-colors"
                            title="Edit story"
                          >
                            <EditIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteStory((story as any).dbId || story.id, story.title)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-50 p-2 rounded transition-colors"
                            title="Delete story"
                          >
                            <DeleteIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render Analytics Tab
  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      {/* Pronunciation Analytics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Pronunciation Accuracy Trends</h3>
        {pronunciationTrends.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <span className="text-6xl mb-4 block">📊</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pronunciation Data</h3>
            <p className="text-gray-600">
              Pronunciation trends will appear here after students complete reading sessions with pronunciation assessments.
            </p>
          </div>
        ) : (
          <PronunciationChart 
            trendData={pronunciationTrends} 
            assessments={[]} 
            type="trend"
          />
        )}
      </div>

      {/* Engagement Analytics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Student Engagement Trends</h3>
        {engagementData.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <span className="text-6xl mb-4 block">📈</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Engagement Data</h3>
            <p className="text-gray-600">
              Engagement trends will appear here after students complete reading sessions.
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280"
                fontSize={12}
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis stroke="#6b7280" fontSize={12} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px'
                }}
                formatter={(value: any) => `${Math.round(value)}%`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="averageEngagement"
                stroke="#3b82f6"
                strokeWidth={3}
                name="Engagement %"
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="completionRate"
                stroke="#10b981"
                strokeWidth={2}
                name="Completion %"
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Agent Usage */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">AI Persona Usage Distribution</h3>
        {agentUsageData.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <span className="text-6xl mb-4 block">🎭</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Persona Usage Data</h3>
            <p className="text-gray-600">
              Persona intervention statistics will appear here after students interact with different AI personas.
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={agentUsageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis 
                dataKey="name" 
                stroke="#6b7280" 
                fontSize={12}
                angle={-15}
                textAnchor="end"
                height={80}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px'
                }}
              />
              <Legend />
              <Bar 
                dataKey="usage_count" 
                fill="#3b82f6" 
                name="Interventions" 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="avg_duration" 
                fill="#10b981" 
                name="Avg Duration (s)" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  // Render Safety Alerts Tab
  const renderAlertsTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Active Safety Alerts</h3>
        </div>
        <div className="p-6">
          {safetyAlerts.length === 0 ? (
            <div className="text-center py-8 bg-green-50 rounded-lg border border-green-200">
              <div className="text-green-600 mb-4">
                <CheckCircleIcon className="w-16 h-16 mx-auto" />
              </div>
              <p className="text-green-800 font-medium">All Clear!</p>
              <p className="text-green-600">No active safety alerts</p>
            </div>
          ) : (
            <div className="space-y-4">
              {safetyAlerts.map(alert => (
                <div key={alert.id} className={`p-4 rounded-lg border-l-4 ${
                  alert.severity === 'critical' ? 'border-red-500 bg-red-50' :
                  alert.severity === 'high' ? 'border-orange-500 bg-orange-50' :
                  alert.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                  'border-blue-500 bg-blue-50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {((alert as any).alert_type || 'unknown').replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-gray-700 mt-2">{(alert as any).message || alert.description || 'No details available'}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="text-xs text-gray-500">
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPromptsTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">📝 Summary Prompt Templates</h3>
          <p className="text-sm text-gray-600 mt-1">
            Customize GPT prompt templates for generating reading session summaries
          </p>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <p className="text-gray-700 mb-4">
              Click the button below to open the full-screen prompt editor where you can customize
              summary generation prompts for both teacher and reader summaries across different grade levels.
            </p>
            <button
              onClick={() => setShowPromptsEditor(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md font-medium"
            >
              📝 Open Prompt Editor
            </button>
          </div>
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">ℹ️ About Prompt Templates</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Edit prompts for <strong>Teacher</strong> (detailed analysis) and <strong>Reader</strong> (student-friendly) summaries</li>
              <li>Customize by grade level: K-2, 3-5, 6-8, 9-12, or set defaults for all grades</li>
              <li>Use variable placeholders like {'{age}'}, {'{story_title}'}, {'{rubric_json}'} to include session data</li>
              <li>Changes create new versions - previous versions are preserved</li>
              <li>Reset to original defaults anytime</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Enhanced Teacher Dashboard</h1>
              {safetyAlerts.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {safetyAlerts.length} Alert{safetyAlerts.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {onReturnToHome && (
                <button
                  onClick={onReturnToHome}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  title="Return to Role Selection"
                >
                  <HomeIcon className="w-4 h-4 mr-2" />
                  Home
                </button>
              )}
              <button
                onClick={() => setShowConfig(!showConfig)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="System Settings"
              >
                <SettingsIcon className="w-6 h-6 text-gray-600" />
              </button>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'sessions', label: 'Sessions', icon: '📊' },
              { key: 'stories', label: 'Stories', icon: '📚' },
              { key: 'analytics', label: 'Analytics', icon: '📈' },
              { key: 'prompts', label: 'Summary Prompts', icon: '📝' },
              { key: 'alerts', label: 'Safety Alerts', icon: '🛡️' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.key === 'alerts' && safetyAlerts.length > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                    {safetyAlerts.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'sessions' && renderSessionsTab()}
        {activeTab === 'stories' && renderStoriesTab()}
        {activeTab === 'analytics' && renderAnalyticsTab()}
        {activeTab === 'prompts' && renderPromptsTab()}
        {activeTab === 'alerts' && renderAlertsTab()}
      </main>

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50 animate-slide-in`}>
          <div className="flex items-center gap-2">
            <span>{notification.type === 'success' ? '✓' : '✗'}</span>
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSessionId && (
        <SessionDetailComponent
          sessionId={selectedSessionId}
          storySessionId={selectedStorySessionId}
          onClose={() => {
            setSelectedSessionId(null);
            setSelectedStorySessionId(null);
          }}
        />
      )}

      {/* Add Story Popup Modal - Two Page System */}
      {showAddStoryPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {editingStory 
                    ? 'Edit Story' 
                    : (popupPage === 1 ? 'Upload Story' : 'Story Details')
                  }
                </h2>
                {popupPage === 2 && !editingStory && (
                  <button
                    onClick={() => setPopupPage(1)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Re-upload File
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setShowAddStoryPopup(false);
                  clearStoryForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <CloseIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              {popupPage === 1 ? (
                // Page 1: Grade Selection and File Upload
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <div className="max-w-md w-full text-center">
                      <label htmlFor="popup-grade-select" className="block text-lg font-bold text-gray-700 mb-4">
                        Select Grade Level
                      </label>
                      <select
                        id="popup-grade-select"
                        value={selectedGrade}
                        onChange={(e) => setSelectedGrade(e.target.value)}
                        className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                        disabled={isUploading}
                      >
                        <option value="" disabled>Choose Grade</option>
                        <option value="Grade K-1">Grade K-1</option>
                        <option value="Grade 2-3">Grade 2-3</option>
                        <option value="Grade 4-5">Grade 4-5</option>
                        <option value="Grade 6-7">Grade 6-7</option>
                        <option value="Grade 8-9">Grade 8-9</option>
                        <option value="Grade 10-12">Grade 10-12</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <div className="relative max-w-lg w-full">
                      <input
                        type="file"
                        accept=".txt"
                        onChange={handleStoryPreview}
                        className="hidden"
                        id="popup-story-upload"
                        disabled={isPreviewing}
                      />
                      <label
                        htmlFor="popup-story-upload"
                        className={`block w-full p-12 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                          isPreviewing 
                            ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                            : 'border-green-300 hover:border-green-500'
                        }`}
                        style={{ backgroundColor: isPreviewing ? undefined : '#e6ffe6' }}
                      >
                        <div className="text-green-600 mb-4">
                          <UploadIcon className="w-16 h-16 mx-auto" />
                        </div>
                        <div className="text-xl font-medium text-gray-700 mb-3">
                          Click to upload story file
                        </div>
                        <div className="text-base text-gray-500 mb-2">
                          Supports .txt files
                        </div>
                        <div className="text-sm text-gray-400">
                          Maximum file size: 10MB
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {isPreviewing && (
                    <div className="text-center">
                      <div className="inline-flex items-center px-6 py-3 bg-green-100 rounded-lg">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600 mr-3"></div>
                        <span className="text-green-800 text-lg">Reading story content...</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Page 2: Story Details Form
                <div className="space-y-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700 w-40">
                            Grade Level
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={selectedGrade}
                              onChange={(e) => handleGradeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                              <option value="" disabled>Choose Grade</option>
                              <option value="Grade K-1">Grade K-1</option>
                              <option value="Grade 2-3">Grade 2-3</option>
                              <option value="Grade 4-5">Grade 4-5</option>
                              <option value="Grade 6-7">Grade 6-7</option>
                              <option value="Grade 8-9">Grade 8-9</option>
                              <option value="Grade 10-12">Grade 10-12</option>
                            </select>
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Story Title
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={uploadedStoryTitle}
                              onChange={(e) => setUploadedStoryTitle(e.target.value)}
                              placeholder="Enter story title"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Reading Level
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={readingLevel}
                              onChange={(e) => setReadingLevel(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                              <option value="" disabled>Select Reading Level</option>
                              <option value="Beginner">Beginner</option>
                              <option value="Intermediate">Intermediate</option>
                              <option value="Advanced">Advanced</option>
                            </select>
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Lexile Level
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={lexileLevel}
                              onChange={(e) => setLexileLevel(e.target.value)}
                              placeholder="e.g., 450L, 680L"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Tags
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={tags}
                              onChange={(e) => setTags(e.target.value)}
                              placeholder="e.g., adventure, friendship, animals (comma-separated)"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Estimated Reading Time
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={estimatedReadingTime}
                              readOnly
                              placeholder="Auto-calculated"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                            />
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Author
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={author}
                              onChange={(e) => setAuthor(e.target.value)}
                              placeholder="Enter author name"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700">
                            Upload Time
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-600 py-2">
                              {new Date().toLocaleString()}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 bg-gray-100 font-medium text-gray-700 align-top">
                            Story Content
                          </td>
                          <td className="px-4 py-3">
                            <textarea
                              value={uploadedStoryContent}
                              onChange={(e) => setUploadedStoryContent(e.target.value)}
                              placeholder="Story content will appear here..."
                              rows={10}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-vertical"
                            />
                            <div className="mt-2 text-xs text-gray-500">
                              Character count: {uploadedStoryContent.length}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
              {popupPage === 1 ? (
                // Page 1 Footer
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddStoryPopup(false);
                      clearStoryForm();
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                // Page 2 Footer
                <>
                  <div className="flex justify-between items-center w-full">
                    <div>
                      {!editingStory && (
                        <button
                          onClick={() => setPopupPage(1)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Re-upload File
                        </button>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowAddStoryPopup(false);
                          clearStoryForm();
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={editingStory ? handleUpdateStory : handleStoryUpload}
                        disabled={isUploading || !uploadedStoryTitle.trim() || !selectedGrade}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                          isUploading || !uploadedStoryTitle.trim() || !selectedGrade
                            ? 'bg-gray-400 cursor-not-allowed text-white'
                            : (editingStory ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white')
                        }`}
                      >
                        {isUploading && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        )}
                        {isUploading 
                          ? (editingStory ? 'Updating...' : 'Uploading...') 
                          : (editingStory ? 'Update Story' : 'Upload Story')
                        }
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Upload Modal */}
      {showBatchUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Batch Upload Stories</h2>
              <button
                onClick={() => setShowBatchUploadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <CloseIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left side - Input */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload File or Paste Content
                    </label>
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleBatchFileUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-4"
                    />
                    <textarea
                      value={batchUploadContent}
                      onChange={(e) => handleBatchContentChange(e.target.value)}
                      placeholder="Paste your batch content here or upload a file above..."
                      rows={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-2">Expected format:</p>
                    <div className="bg-gray-50 p-3 rounded border text-xs font-mono">
                      Story 1:<br/>
                      Grade Level : Grade 2-3<br/>
                      Title : Story Title<br/>
                      Reading Level: beginner<br/>
                      Author : Author Name<br/>
                      Lexile: 300L<br/>
                      Tags: tag1, tag2, tag3<br/>
                      <br/>
                      Story: Once upon a time...
                    </div>
                  </div>
                </div>
                
                {/* Right side - Preview */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parsed Stories ({parsedStories.length})
                    </label>
                    <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
                      {parsedStories.length === 0 ? (
                        <div className="p-4 text-gray-500 text-center">
                          No stories parsed yet. Add content to see preview.
                        </div>
                      ) : (
                        <div className="space-y-2 p-2">
                          {parsedStories.map((story, index) => (
                            <div key={index} className="bg-blue-50 p-3 rounded border">
                              <h4 className="font-medium text-gray-900">{story.title}</h4>
                              <div className="text-xs text-gray-600 mt-1">
                                <span className="inline-block bg-blue-100 px-2 py-1 rounded mr-2">{story.gradeLevel}</span>
                                <span className="inline-block bg-green-100 px-2 py-1 rounded mr-2">{story.readingLevel}</span>
                                {story.lexile && <span className="inline-block bg-purple-100 px-2 py-1 rounded mr-2">{story.lexile}</span>}
                              </div>
                              <p className="text-xs text-gray-700 mt-2">
                                Author: {story.author || 'Not specified'} | 
                                Tags: {story.tags || 'None'} | 
                                Content: {story.content ? `${story.content.substring(0, 50)}...` : 'No content'}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {parsedStories.length > 0 && `Ready to upload ${parsedStories.length} stories`}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBatchUploadModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={processBatchUpload}
                  disabled={isBatchUploading || parsedStories.length === 0}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    isBatchUploading || parsedStories.length === 0
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isBatchUploading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {isBatchUploading ? 'Uploading...' : `Upload ${parsedStories.length} Stories`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Configuration Panel */}
      {showConfig && (
        <SystemConfigPanel onClose={() => setShowConfig(false)} />
      )}

      {/* Summary Prompts Editor */}
      {showPromptsEditor && (
        <SummaryPromptsEditor onClose={() => setShowPromptsEditor(false)} />
      )}
    </div>
  );
}